import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { PRINTER_FORMATS, CURRENCY_SYMBOL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Printer, Droplets, ArrowLeft, Loader2 } from "lucide-react";

export default function PrintSlip() {
  const { orderId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [format, setFormat] = useState("80mm");

  useEffect(() => {
    api.get(`/public/order/${orderId}`).then((r) => { setData(r.data); setFormat(r.data.mill.printer_format || "80mm"); });
  }, [orderId]);

  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const { order, mill } = data;
  const trackUrl = `${window.location.origin}/track/${order.order_code}`;
  const isA4 = format === "A4";

  return (
    <div className="min-h-screen bg-secondary/40">
      {/* Controls */}
      <div className="no-print sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => window.close()} data-testid="print-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> {t("back")}</Button>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">{t("format")}:</span>
            {PRINTER_FORMATS.map((f) => (
              <button key={f} data-testid={`format-${f}`} onClick={() => setFormat(f)}
                className={`rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors ${format === f ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                {f}
              </button>
            ))}
            <Button data-testid="do-print-btn" size="sm" onClick={() => window.print()} className="rounded-lg bg-primary hover:bg-olive-hover"><Printer className="mr-1 h-4 w-4" /> {t("print")}</Button>
          </div>
        </div>
      </div>

      {/* Slip preview */}
      <div className="flex justify-center px-4 py-8">
        <div id="slip" data-testid="slip-preview" className={`slip slip-${format} bg-white p-4 text-black shadow-lg ${isA4 ? "p-10" : ""}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <SlipContent order={order} mill={mill} trackUrl={trackUrl} isA4={isA4} format={format} t={t} />
        </div>
      </div>
    </div>
  );
}

function SlipContent({ order, mill, trackUrl, isA4, format, t }) {
  const Line = () => <div className="my-2 border-t border-dashed border-black/40" />;
  const inv = order.invoice && order.price_per_kg > 0;
  const sym = CURRENCY_SYMBOL[order.invoice_currency] || "€";
  const fmtCost = (kg) => `${sym}${Math.round((kg || 0) * order.price_per_kg)}`;
  return (
    <div className={isA4 ? "text-sm" : "text-xs"} style={format === "58mm" ? { fontSize: "10px" } : undefined}>
      <div className="text-center">
        <div className="mb-1 flex items-center justify-center gap-1.5">
          {mill.logo ? <img src={mill.logo} alt="" data-testid="slip-logo" className={isA4 ? "h-8 w-8 object-contain" : "h-6 w-6 object-contain"} /> : <Droplets className={isA4 ? "h-7 w-7" : "h-5 w-5"} />}
          <span className={`font-bold uppercase ${isA4 ? "text-2xl" : "text-base"}`} style={{ fontFamily: "Outfit, sans-serif" }}>{mill.mill_name}</span>
        </div>
        {mill.address && <p className="text-[10px]">{mill.address}</p>}
        {mill.phone && <p className="text-[10px]">{mill.phone}</p>}
      </div>
      <Line />
      <div className="flex justify-between"><span>{t("order_code")}</span><span className="font-bold">{order.order_code}</span></div>
      <div className="flex justify-between"><span>Date</span><span>{new Date(order.created_at).toLocaleDateString()}</span></div>
      <div className="flex justify-between"><span>{t("client_name")}</span><span className="font-bold">{order.client_name}</span></div>
      {order.client_phone && <div className="flex justify-between"><span>{t("phone")}</span><span>{order.client_phone}</span></div>}
      <Line />
      {order.crates?.length > 0 ? (
        <>
          <div className="flex font-bold">
            <span className="flex-1">{t("crate")}</span>
            <span className="w-16 text-right">{t("weight_kg")}</span>
            {inv && <span className="w-20 text-right">{t("cost")}</span>}
          </div>
          {order.crates.map((c) => (
            <div key={c.number} className="flex">
              <span className="flex-1">#{c.number}</span>
              <span className="w-16 text-right">{Math.round(c.weight)} kg</span>
              {inv && <span className="w-20 text-right">{fmtCost(c.weight)}</span>}
            </div>
          ))}
        </>
      ) : (
        <div className="flex justify-between"><span>{t("crate_count")}</span><span>{order.crate_count}</span></div>
      )}
      <Line />
      <div className="flex text-base font-bold">
        <span className="flex-1">{t("total")}</span>
        <span className="w-16 text-right">{Math.round(order.total_weight)} kg</span>
        {inv && <span className="w-20 text-right">{fmtCost(order.total_weight)}</span>}
      </div>
      {inv && <div className="flex justify-between text-[10px] text-black/60"><span>{t("price_per_kg")}</span><span>{sym}{order.price_per_kg}/kg</span></div>}
      {order.show_yield && <div className="flex justify-between"><span>{t("est_yield")}</span><span>~{Math.round(order.estimated_yield)} {t("liters")}</span></div>}
      {order.notes && <><Line /><p className="text-[10px]">{t("notes")}: {order.notes}</p></>}
      <Line />
      <div className="flex flex-col items-center py-2">
        <QRCodeSVG value={trackUrl} size={isA4 ? 140 : 96} level="M" data-testid="slip-qr" />
        <p className="mt-2 text-center text-[10px]">{t("scan_to_track")}</p>
        <p className="mt-0.5 text-center text-[9px] text-black/50">{order.order_code}</p>
      </div>
      <p className="mt-2 text-center text-[10px] font-bold">{t("thank_you")}</p>
    </div>
  );
}
